/* eslint-disable react-hooks/exhaustive-deps */
import { FilterOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Input,
  Popover,
  Table,
  TableColumnType,
  Tag,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { IQCFilter, IQCRequest } from "../../models/Qc";
import { useGetQCRequest } from "../../query/QcQuery";
import { useNavigate } from "react-router-dom";

const QualityControlRequest = () => {
  const [params, setParams] = useState<IQCFilter>({
    page: 1,
    limit: 10,
    status: "PENDING,PROCESSING",
  });
  const navigate = useNavigate();

  const { data: qcRequestData, isLoading } = useGetQCRequest(params);

  const columns: TableColumnType<IQCRequest>[] = [
    {
      title: "STT",
      key: "index",
      align: "center",
      render: (_: any, __: IQCRequest, index: number) =>
        (params.page! - 1) * params.limit! + index + 1,
    },
    {
      title: "Tên yêu cầu",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <Typography.Text strong style={{ color: "#1890ff" }}>
          {name}
        </Typography.Text>
      ),
    },
    {
      title: "AWB",
      dataIndex: "awb",
      key: "awb",
      align: "center",
      render: (awb: string) => <Typography.Text strong>{awb}</Typography.Text>,
    },
    {
      title: "Mức độ ưu tiên",
      dataIndex: "priority",
      key: "priority",
      align: "center",
      width: 150,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityLabel(priority)}
        </Tag>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      align: "center",

      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
      ),
    },
    {
      title: "Người tạo",
      dataIndex: ["createdByUser", "fullName"],
      key: "createdBy",
      align: "center",
    },
    {
      title: "Thao tác",
      dataIndex: "action",
      key: "action",
      align: "center",

      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => navigate(`${record.id}`)}
        >
          Xem chi tiết
        </Button>
      ),
    },
  ];

  const handleChangeAwb = useCallback(
    debounce((value: string) => {
      setParams((prev) => ({ ...prev, awb: value, page: 1 }));
    }, 500),
    [setParams],
  );

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "HIGH":
        return "Cao";
      case "MEDIUM":
        return "Trung bình";
      case "LOW":
        return "Thấp";
      default:
        return priority;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "HIGH":
        return "red";
      case "MEDIUM":
        return "orange";
      case "LOW":
        return "green";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Chờ xử lý";
      case "PROCESSING":
        return "Đang xử lý";
      case "DONE":
        return "Hoàn thành";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "gold";
      case "PROCESSING":
        return "blue";
      case "DONE":
        return "green";
      default:
        return "default";
    }
  };

  return (
    <div className="p-4">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "center",
          textAlign: "center",
          marginBottom: 24,
          justifyContent: "center",
        }}
      >
        <Typography.Title
          style={{
            textTransform: "uppercase",
            margin: 0,
            fontFamily: "Chakra Petch",
            fontSize: 18,
            fontWeight: 700,
          }}
          level={4}
        >
          Yêu cầu kiểm tra chất lượng
        </Typography.Title>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Input
            placeholder="Nhập AWB"
            style={{
              width: 350,
              borderRadius: 6,
            }}
            allowClear
            onChange={(e) => handleChangeAwb(e.target.value)}
          />

          <Popover
            content={
              <div style={{ padding: 8 }}>
                <Checkbox.Group
                  value={params.priority?.split(",") || []}
                  onChange={(checkedValues) => {
                    const priorityString = (checkedValues as string[]).join(
                      ",",
                    );
                    setParams((prev) => ({
                      ...prev,
                      priority: priorityString || undefined,
                      page: 1,
                    }));
                  }}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <Checkbox value="HIGH">
                    <span style={{ color: "#d4380d" }}>● Cao</span>
                  </Checkbox>
                  <Checkbox value="MEDIUM">
                    <span style={{ color: "#fa8c16" }}>● Trung bình</span>
                  </Checkbox>
                  <Checkbox value="LOW">
                    <span style={{ color: "#52c41a" }}>● Thấp</span>
                  </Checkbox>
                </Checkbox.Group>
              </div>
            }
            title={<strong>Lọc theo mức độ ưu tiên</strong>}
            trigger="click"
            placement="bottomLeft"
          >
            <Button
              type="default"
              icon={<FilterOutlined />}
              style={{
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Lọc
              {params.priority && (
                <Tag color="blue" style={{ marginRight: 0 }}>
                  {params.priority.split(",").length}
                </Tag>
              )}
            </Button>
          </Popover>

          <Popover
            content={
              <div style={{ padding: 8 }}>
                <Checkbox.Group
                  value={params.status?.split(",") || []}
                  onChange={(checkedValues) => {
                    const statusString = (checkedValues as string[]).join(",");
                    setParams((prev) => ({
                      ...prev,
                      status: statusString || undefined,
                      page: 1,
                    }));
                  }}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <Checkbox value="PENDING">
                    <span style={{ color: "#d48806" }}>● Chờ xử lý</span>
                  </Checkbox>
                  <Checkbox value="PROCESSING">
                    <span style={{ color: "#1890ff" }}>● Đang xử lý</span>
                  </Checkbox>
                  <Checkbox value="DONE">
                    <span style={{ color: "#52c41a" }}>● Hoàn thành</span>
                  </Checkbox>
                </Checkbox.Group>
              </div>
            }
            title={<strong>Lọc theo trạng thái</strong>}
            trigger="click"
            placement="bottomLeft"
          >
            <Button
              type="default"
              icon={<FilterOutlined />}
              style={{
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Trạng thái
              {params.status && (
                <Tag color="blue" style={{ marginRight: 0 }}>
                  {params.status.split(",").length}
                </Tag>
              )}
            </Button>
          </Popover>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={qcRequestData?.data || []}
        loading={isLoading}
        rowKey="id"
        bordered
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: qcRequestData?.pagination?.total || 0,
          onChange: (p, ps) => {
            setParams((prev) => ({ ...prev, page: p, limit: ps }));
          },
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} yêu cầu`,
        }}
        locale={{
          emptyText: (
            <div className="text-center text-gray-500 py-8">
              Không có yêu cầu kiểm tra chất lượng nào
            </div>
          ),
        }}
      />
    </div>
  );
};

export default QualityControlRequest;
